// Minimal client-side router. Pattern-matching against window.location.pathname.
// Routes register Component factories; auth guard redirects when needed.

type RouteFactory = (params: Record<string, string>) => Component;
// A guard either allows the route, redirects elsewhere, or renders a screen in
// place (keeping the URL) — the last is how a capability denial shows
// the friendly forbidden surface instead of bouncing the user.
type GuardResult =
  | { allowed: true }
  | { allowed: false; redirectTo: string }
  | { allowed: false; render: RouteFactory };
type Guard = (path: string) => GuardResult;

export interface Component {
  mount(host: HTMLElement): void;
  unmount?(): void;
}

// lazyComponent defers loading a page's module until it is mounted, so the page
// splits into its own chunk (fetched on first navigation) instead of riding the
// main bundle. `load` resolves to the ready-to-mount Component. A failed chunk
// fetch leaves the shell empty rather than throwing; unmounting before the chunk
// resolves cancels the mount.
export function lazyComponent(load: () => Promise<Component>): Component {
  let inner: Component | null = null;
  let current: HTMLElement | null = null;
  let disposed = false;
  return {
    mount(h: HTMLElement) {
      current = h;
      disposed = false;
      void load()
        .then((c) => {
          if (disposed || current !== h) return;
          inner = c;
          c.mount(h);
        })
        .catch(() => {
          /* a failed chunk load leaves the shell empty */
        });
    },
    unmount() {
      disposed = true;
      inner?.unmount?.();
      inner = null;
      current = null;
    },
  };
}

type Route = {
  pattern: RegExp;
  paramNames: string[];
  factory: RouteFactory;
  isPublic: boolean;
};

const routes: Route[] = [];
let guard: Guard | null = null;
let host: HTMLElement | null = null;
let active: Component | null = null;
let notFoundFactory: RouteFactory = () => textComponent('Not Found');

export function registerRoute(path: string, factory: RouteFactory, isPublic = false): void {
  const paramNames: string[] = [];
  const re = new RegExp(
    '^' +
      path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name) => {
        paramNames.push(name);
        return '([^/]+)';
      }) +
      '/?$'
  );
  routes.push({ pattern: re, paramNames, factory, isPublic });
}

export function setNotFound(factory: RouteFactory): void {
  notFoundFactory = factory;
}

export function setAuthGuard(g: Guard): void {
  guard = g;
}

export function initRouter(mountPoint: HTMLElement): void {
  host = mountPoint;
  window.addEventListener('popstate', renderRoute);
  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement | null)?.closest?.('a[href^="/"]');
    if (!target) return;
    const href = (target as HTMLAnchorElement).getAttribute('href');
    if (!href || href.startsWith('//')) return;
    if ((target as HTMLAnchorElement).target === '_blank') return;
    e.preventDefault();
    navigate(href);
  });
  renderRoute();
}

export function navigate(path: string): void {
  if (location.pathname + location.search !== path) {
    history.pushState(null, '', path);
  }
  renderRoute();
}

// rerender re-mounts the current route in place (no history change). Used when
// the locale changes so the page re-reads its strings via i18n `t()`.
export function rerender(): void {
  renderRoute();
}

function renderRoute(): void {
  if (!host) return;
  const mountPoint = host;
  const path = location.pathname;

  const mount = (factory: RouteFactory, params: Record<string, string>): void => {
    if (active && active.unmount) active.unmount();
    mountPoint.innerHTML = '';
    active = factory(params);
    active.mount(mountPoint);
  };

  if (guard) {
    const result = guard(path);
    if (!result.allowed) {
      if ('render' in result) {
        mount(result.render, {});
        return;
      }
      if (path !== result.redirectTo) {
        navigate(result.redirectTo);
        return;
      }
    }
  }

  const match = routes.find((r) => r.pattern.test(path));
  const params: Record<string, string> = {};
  let factory: RouteFactory = notFoundFactory;
  if (match) {
    const captured = match.pattern.exec(path);
    if (captured) {
      match.paramNames.forEach((name, i) => {
        params[name] = decodeURIComponent(captured[i + 1] ?? '');
      });
    }
    factory = match.factory;
  }
  mount(factory, params);
}

function textComponent(text: string): Component {
  return {
    mount(h) {
      const p = document.createElement('p');
      p.textContent = text;
      p.style.padding = 'var(--space-xl)';
      h.appendChild(p);
    },
  };
}
